import { formatEnum } from 'components/formatter.utils';
import type { NotVerifiedAsset } from 'components/market/asset/AssetLabelService';
import { createAssetAutocompleteProps, useAssetPaginatedOptions } from 'components/market/asset/AssetService';
import type { CreateSubAccountIdAutocompleteOptionsInputAccount } from 'components/portfolio/account/AccountService';
import { calculateAvailableAccountSubAccountIds } from 'components/portfolio/PortfolioService';
import type { Filter } from 'components/technical/GTable/GTable.props';
import GMultiAutocomplete from 'components/technical/inputs/Autocomplete/GMultiAutocomplete';
import type { GMultiAutocompleteProps } from 'components/technical/inputs/Autocomplete/GMultiAutocomplete.props';
import StaticMultiAutocomplete from 'components/technical/inputs/Autocomplete/StaticMultiAutocomplete';
import type { StaticMultiAutocompleteProps } from 'components/technical/inputs/Autocomplete/StaticMultiAutocomplete.props';
import type {
  StaticAutocompleteOption,
  StaticAutocompleteProps,
} from 'components/technical/inputs/Autocomplete/StaticSingleAutocomplete.props';
import DateRangeInput, { type DateRangeInputProps } from 'components/technical/inputs/date/DateRangeInput';
import GInput from 'components/technical/inputs/GInput/GInput';
import type { GInputProps } from 'components/technical/inputs/GInput/GInput.props';
import Loader from 'components/technical/Loader/Loader';
import type { PortfolioFilters } from 'components/technical/PortfolioFilterDrawer/UsePortfolioFilters';
import type { Dayjs } from 'dayjs';
import { IOrderSide, ITransactionLegType, ITransactionStatus, IUserTransactionType } from 'generated/graphql';
import { useCallback } from 'react';

export const transactionTypes: StaticAutocompleteOption<string>[] = [
  ...Object.entries(IUserTransactionType).map(([name, value]) => ({
    label: formatEnum(value),
    value: value,
    key: name,
    searchText: formatEnum(value),
  })),
];

export const orderSides: StaticAutocompleteOption<string>[] = Object.entries(IOrderSide).map(([name, value]) => ({
  label: name,
  value: value,
  key: name,
  searchText: name,
}));

export const transactionStatuses: StaticAutocompleteOption<ITransactionStatus>[] = Object.entries(
  ITransactionStatus
).map(([name, value]) => ({
  label: name,
  value: value,
  key: name,
  searchText: name,
}));

export const legTypes: StaticAutocompleteOption<ITransactionLegType>[] = Object.entries(ITransactionLegType).map(
  ([name, value]) => ({
    label: name,
    value: value,
    key: name,
    searchText: name,
  })
);

export const createFilters = ({
  dateRange,
  setDateRange,
  isValidDateRange,
  transactionType,
  setTransactionTransactionType,
  assetFilter,
  setAssetFilter,
  width,
  accountOptions,
  subAccountOptions,
  accountFilter,
  setAccountFilter,
  subAccountFilter,
  setSubAccountFilter,
  // existingTags,
  // filterTags,
  // setFilterTags,
  commentFilter,
  setCommentFilter,
  externalIdFilter,
  setExternalIdFilter,
  debouncedCommentFilter,
  debouncedExternalIdFilter,
  statusFilter,
  setStatusFilter,
  legType,
  setLegType,
  side,
  setSide,
  portfolioFilters,
}: {
  dateRange: [Dayjs, Dayjs] | null;
  setDateRange: (dateRange: [Dayjs, Dayjs] | null) => void;
  isValidDateRange: boolean;
  transactionType: string[];
  setTransactionTransactionType: (transactionType: string[]) => void;
  assetFilter: (NotVerifiedAsset & { id: string })[];
  setAssetFilter: (assetFilter: (NotVerifiedAsset & { id: string })[]) => void;
  width: 'normal';
  accountOptions: StaticAutocompleteProps<string>;
  subAccountOptions: StaticAutocompleteProps<string>;
  existingTags: string[];
  // filterTags: string[];
  // setFilterTags: (filterTags: string[]) => void;
  commentFilter: string;
  setCommentFilter: (commentFilter: string) => void;
  externalIdFilter: string;
  setExternalIdFilter: (externalIdFilter: string) => void;
  debouncedCommentFilter: string;
  debouncedExternalIdFilter: string;
  statusFilter: ITransactionStatus[];
  setStatusFilter: (statusFilter: ITransactionStatus[]) => void;
  legType: ITransactionLegType[];
  setLegType: (legType: ITransactionLegType[]) => void;
  side: string[];
  setSide: (side: string[]) => void;
  accountFilter: string[];
  setAccountFilter: (accountFilter: string[]) => void;
  subAccountFilter: string[];
  setSubAccountFilter: (subAccountFilter: string[]) => void;
  portfolioFilters: PortfolioFilters;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
}): Filter<any, any>[] => {
  const { getOptions: getAssetOptions } = useAssetPaginatedOptions();
  const getConstrainedAssetOptions = useCallback(
    async ({
      input,
      signal,
      paginationState,
    }: {
      input: string;
      signal: AbortSignal;
      paginationState: unknown;
    }) => {
      const result = await getAssetOptions({ input, signal, paginationState });
      const globalAssetIds = portfolioFilters.assetIds ?? [];

      if (globalAssetIds.length > 0) {
        return {
          ...result,
          data: result.data.filter((asset) => globalAssetIds.includes(asset.id)),
        };
      }

      return result;
    },
    [getAssetOptions, portfolioFilters.assetIds]
  );
  return [
    {
      component: DateRangeInput,
      value: dateRange,
      width: 'xl',
      error: isValidDateRange ? undefined : 'Invalid date',
      onChange: setDateRange,
      label: 'Date',
      defaultValue: null,
      defaultFilter: true,
    } satisfies Filter<DateRangeInputProps>,
    {
      component: StaticMultiAutocomplete,
      options: transactionTypes,
      value: transactionType,
      width,
      limitTags: 1,
      onChange: setTransactionTransactionType,
      label: 'Type',
      defaultValue: [],
      defaultFilter: true,
    } satisfies Filter<StaticMultiAutocompleteProps<string>>,
    {
      component: GMultiAutocomplete,
      getOptions: getConstrainedAssetOptions,
      ...createAssetAutocompleteProps(),
      value: assetFilter,
      width,
      menuWidth: 'xl2',
      onChange: setAssetFilter,
      label: 'Asset',
      defaultValue: [],
      defaultFilter: true,
    } satisfies Filter<GMultiAutocompleteProps<NotVerifiedAsset & { id: string }>>,
    {
      label: 'Order side',
      component: StaticMultiAutocomplete,
      options: orderSides,
      value: side,
      width,
      limitTags: 2,
      onChange: setSide,
      defaultValue: [],
    } satisfies Filter<StaticMultiAutocompleteProps<string>>,
    {
      label: 'Account',
      component: StaticMultiAutocomplete,
      disabled: accountOptions.options.length === 0,
      ...accountOptions,
      value: accountFilter,
      width,
      limitTags: 2,
      onChange: setAccountFilter,
      defaultFilter: true,
      defaultValue: [],
    } satisfies Filter<StaticMultiAutocompleteProps<string>>,
    {
      label: 'Sub-account',
      component: StaticMultiAutocomplete,
      disabled: subAccountOptions.options.length === 0,
      ...subAccountOptions,
      value: subAccountFilter,
      width,
      limitTags: 2,
      onChange: setSubAccountFilter,
      defaultValue: [],
    } satisfies Filter<StaticMultiAutocompleteProps<string>>,
    // {
    //   label: 'Tags',
    //   component: StaticMultiAutocomplete,
    //   limitTags: 2,
    //   options: existingTags.map((tag) => ({
    //     label: tag,
    //     value: tag,
    //     key: tag,
    //     searchText: tag,
    //   })),
    //   value: filterTags,
    //   width,
    //   showChipTooltips: true,
    //   onChange: setFilterTags,
    //   defaultValue: [],
    // } satisfies Filter<StaticMultiAutocompleteProps<string>>,
    {
      label: 'Comment',
      component: GInput,
      width,
      value: commentFilter,
      endDecorator: commentFilter !== debouncedCommentFilter ? <Loader variant="small" /> : null,
      onChange: (val: string | null) => setCommentFilter(val ?? ''),
      defaultValue: '',
    } satisfies Filter<GInputProps>,
    {
      label: 'External id',
      component: GInput,
      width,
      value: externalIdFilter,
      endDecorator: externalIdFilter !== debouncedExternalIdFilter ? <Loader variant="small" /> : null,
      onChange: (val: string | null) => setExternalIdFilter(val ?? ''),
      defaultValue: '',
    } satisfies Filter<GInputProps>,
    {
      component: StaticMultiAutocomplete,
      options: transactionStatuses,
      value: statusFilter,
      width,
      limitTags: 1,
      onChange: setStatusFilter,
      label: 'Status',
      defaultValue: [],
      defaultFilter: false,
    } satisfies Filter<StaticMultiAutocompleteProps<ITransactionStatus>>,
    {
      component: StaticMultiAutocomplete,
      options: legTypes,
      value: legType,
      width,
      limitTags: 1,
      onChange: setLegType,
      label: 'Leg type',
      defaultValue: [],
      defaultFilter: false,
    } satisfies Filter<StaticMultiAutocompleteProps<ITransactionLegType>>,
  ];
};

export const calculateFilters = ({
  accounts,
  subFunds,
  portfolioFilters,
  accountFilter,
  subAccountFilter,
}: {
  accounts: CreateSubAccountIdAutocompleteOptionsInputAccount[];
  subFunds: {
    id: number;
    accounts: {
      id: string;
    }[];
    subAccounts: {
      id: string;
    }[];
  }[];
  portfolioFilters: PortfolioFilters;
  accountFilter: string[];
  subAccountFilter: string[];
}): {
  accountFilter: string[];
  subAccountFilter: string[];
  avAccountIds: string[];
  avSubAccountIds: string[];
} => {
  const accToSubAccs = new Map(accounts.map((acc) => [acc.id, acc.subAccounts.map((subAcc) => subAcc.id)]));
  const { filteredAccountIds: avAccountIds, filteredSubAccountIds: avGlobalSubAccountIds } =
    calculateAvailableAccountSubAccountIds({
      allSubFunds: subFunds,
      allAccounts: accounts,
      subFundIds: portfolioFilters.subFundIds ?? [],
      accountIds: portfolioFilters.accountIds ?? [],
      subAccountIds: portfolioFilters.subAccountIds ?? [],
    });

  const newAccountFilter = accountFilter.filter((accId) => avAccountIds.includes(accId));
  const avSubAccountIds = avGlobalSubAccountIds.filter((subAccId) => {
    if (newAccountFilter.length === 0) {
      return true;
    }

    return newAccountFilter.some((acc) => accToSubAccs.get(acc)?.includes(subAccId));
  });

  const newSubAccountFilter = subAccountFilter.filter((subAccId) => avSubAccountIds.includes(subAccId));
  return {
    accountFilter: newAccountFilter,
    subAccountFilter: newSubAccountFilter,
    avAccountIds: avAccountIds,
    avSubAccountIds,
  };
};
